<script setup>
import 'devextreme-vue/lookup'
import 'devextreme/ui/slider'
import ArrayStore from 'devextreme/data/array_store'
import DataSource from 'devextreme/data/data_source'
import router from '@/router'
import { useRoute } from 'vue-router'
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxForm } from 'devextreme-vue/form'

import * as dataGrid from '@/composables/data-grid'
import { useNotify } from '@/composables/notify'

import { codeNameDisplayExpr } from '@/composables/label'
import { useValidationTabs } from '@/composables/validation'

import { useGarageStore } from '@/stores/garage.store'
import { useOrderStore } from '@/stores/order.store'
import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useSaleStore } from '@/stores/sale.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { DxTagBox } from 'devextreme-vue'

import CityDropDownBox from '@/components/shared/CityDropDownBox.vue'
import CountryDropDownBox from '@/components/shared/CountryDropDownBox.vue'
import DocumentProgressStatusGrid from '@/components/shared/DocumentProgressStatusGrid.vue'
import ExternalPrivacyDataProcessorGrid from '@/components/shared/ExternalPrivacyDataProcessorGrid.vue'
import InterventionTypeGrid from '@/components/shared/InterventionTypeGrid.vue'
import StateDropDownBox from '@/components/shared/StateDropDownBox.vue'
import ValidationTab from '@/components/shared/ValidationTab.vue'
import { formatBytes } from '@/lib/utils'

const i18n = getI18n()
const route = useRoute()
const webSiteStore = useWebSiteStore()
const garageStore = useGarageStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const orderStore = useOrderStore()
const saleStore = useSaleStore()
const { notifySuccess } = useNotify()

const confirmDialog = ref(false)
const companyDataForm = ref(null)
const companyData = ref(registryStore.companyData)
const logoFile = ref(null)
const logoFileCleared = ref(false)
const logoFileError = ref(null)
const excludedSaleReasons = ref([])
let initialExcludedSaleReasons = []
const saleReasonsDataSource = ref(null)
const validationStatus = ref(null)
const { validationTabIndexErrors, onTabsValidating } = useValidationTabs()
const companyDataFormKey = ref(0)
let selectedTabIndex = 0

const maxLogoSize = window.config?.MAX_LOGO_SIZE
const companyDataValidationGroup = 'companyDataForm'
const requiredValidationRules = [{ type: 'required' }]

const dpo_fields = [
    'dpo_name',
    'dpo_branch_address',
    'dpo_contact_details',
    'dpo_email',
    'dpo_email_certified',
    'dpo_tax_code',
    'dpo_vat_number'
]

const companyDataDefaultCurrency = webSiteStore.currencies.find(i => i.id === companyData.value?.default_currency_ref)
const minPrecision = ref(companyDataDefaultCurrency?.precision_value || dataGrid.currencyPrecision(companyDataDefaultCurrency?.code))

const getArrayFromFlagValue = (value, enumObject) => {
  return Object.values(enumObject)
    .filter(flag => (value & flag) === flag)
}

const deconstructFlags = () => {
  companyData.value.data_controller_data_type = getArrayFromFlagValue(companyData.value.data_controller_data_type, registryStore.dataControllerDataTypes)
  companyData.value.data_controller_processing_methods = getArrayFromFlagValue(companyData.value.data_controller_processing_methods, registryStore.dataControllerProcessingMethods)
  companyData.value.data_controller_legal_basis = getArrayFromFlagValue(companyData.value.data_controller_legal_basis, registryStore.dataControllerLegalBasis)
  companyData.value.data_transfer_mechanisms = getArrayFromFlagValue(companyData.value.data_transfer_mechanisms, registryStore.dataControllerDataTransferMechanisms)
}

onMounted(async() => {
  await registryStore.fetchCompanyDataAsync(true)

  deconstructFlags()
  companyDataForm.value?.instance?.repaint()

  excludedSaleReasons.value = saleStore.saleReasons.filter(r => r.exclude_sdi_submission).map(r => r.id)
  initialExcludedSaleReasons = [...excludedSaleReasons.value]

  saleReasonsDataSource.value = new DataSource({
    store: new ArrayStore({
      key: 'id',
      data: saleStore.getLocalizedSaleReasons()
    }),
    sort: 'code'
  })

  const tab = Number(route.query.tab || 0)
  if (tab) {
    const newQuery = { ...route.query }
    delete newQuery.tab
    router.replace({ query: newQuery })
    goToTab(tab)
  }
})

const onFormReady = (e) => {
  const form = e.component

  const dataTransferMechanismsTagBox = form.getEditor('data_transfer_mechanisms')

  if (dataTransferMechanismsTagBox) {
    dataTransferMechanismsTagBox.option('disabled', companyData.value.data_transfer_outside_eea !== true)
  }

  dpo_fields.forEach(field => {
    const editor = form.getEditor(field)

    if (editor) {
      editor.option('disabled', companyData.value.has_dpo !== true)
    }
  })
}

const goToTab = (index) => {
  const tabPanel = companyDataForm.value?.instance?.getEditor('companyTabs')
  if (tabPanel) {
    selectedTabIndex = index
    tabPanel.option('selectedIndex', index)
  }
}

const searchProp = (obj, key) => {
  let values = []

  for (const prop in obj) {
    if (obj.hasOwnProperty(prop)) {
      if (prop === key) {
        values.push(obj[prop])
      } else if (typeof obj[prop] === 'object') {
        values = values.concat(searchProp(obj[prop], key))
      }
    }
  }

  return values
}

const getFilterBySupplier = (supplierRef) => {
  return supplierRef ? ['supplier_ref', '=', supplierRef] : null
}

const getFilterBySupplierAndSupplierDivision = (supplierRef, supplierDivisionRef = null) => {
  let filter = null
  if (supplierRef) {
    const currentSupplier = registryStore.suppliers.find(s => s.id === supplierRef)
    if (currentSupplier && currentSupplier.supplier_brand_ref) {
      filter = ['supplier_brand_ref', '=', currentSupplier.supplier_brand_ref]
    }
  }
  return filter
}

const updateMinCurrencyPrecision = (currencyId) => {
  const editor = companyDataForm.value?.instance?.getEditor('default_display_price_precision')
  const currency = webSiteStore.currencies.find(i => i.id === currencyId)
  const min_precision = currency?.precision_value || dataGrid.currencyPrecision(currency?.code) || 0
  minPrecision.value = min_precision
  if (editor) {
    if (min_precision > editor.instance().option('value')) {
      editor.instance().option('value', min_precision)
    }
    editor.instance().option('min', minPrecision.value)
  }
}

const updateLookupDataSource = (value, editorField, filters, readOnly = true) => {
  const editor = companyDataForm.value.instance?.getEditor(editorField)
  if (editor) {
    if (readOnly) {
      editor.instance().option('readOnly', !value)
    }

    const editorDataSource = editor.getDataSource()

    if (value && editorDataSource) {
      editorDataSource.filter(filters)
      editorDataSource.load()
    }
  }
}

const onConfirmSave = async() => {
  const validationResult = companyDataForm.value.instance?.validate()
  onTabsValidating(validationResult)
  if (validationResult.isValid) {
    confirmDialog.value = true
  }
}

const updateSaleReasonsAsync = async() => {
  const previous = saleStore.getLocalizedSaleReasons().filter(r => r.exclude_sdi_submission).map(r => r.id)
  const current = excludedSaleReasons.value

  const toEnable = current.filter(id => !previous.includes(id))
  const toDisable = previous.filter(id => !current.includes(id))

  if (toEnable.length === 0 && toDisable.length === 0) { return }

  await saleStore.api.updateExcludedSaleReasonsAsync({ ids: toEnable, ids_disabled: toDisable })
  await saleStore.fetchSaleReasonsAsync()
}

const onSave = async() => {
  const reduceFlags = (val) => Array.isArray(val) ? val.reduce((acc, v) => acc + v, 0) : (val ?? 0)

  try {
    if (JSON.stringify(initialExcludedSaleReasons) !== JSON.stringify(excludedSaleReasons.value)) {
      await updateSaleReasonsAsync()
    }
    const companyDataToSave = {
      ...companyData.value,
      data_controller_data_type: reduceFlags(companyData.value.data_controller_data_type),
      data_controller_processing_methods: reduceFlags(companyData.value.data_controller_processing_methods),
      data_controller_legal_basis: reduceFlags(companyData.value.data_controller_legal_basis),
      data_transfer_mechanisms: reduceFlags(companyData.value.data_transfer_mechanisms),
    }
    delete companyDataToSave.logo_file
    if (logoFileCleared.value && !logoFile.value) { companyDataToSave.logo_file = null }
    await registryStore.api.updateCompanyDataAsync(companyDataToSave)
    if (logoFile.value) {
      await registryStore.api.updateCompanyLogoAsync(companyData.value.id, logoFile.value)
      logoFile.value = null
    }
    logoFileCleared.value = false
    await registryStore.fetchCompanyDataAsync(true)
    companyData.value = registryStore.companyData
    deconstructFlags()
    notifySuccess(i18n.t('common.success_update'))
  } catch (error) {
    validationStatus.value = 'error'

    if (error.response.status === 400) {
      const invalidFields = searchProp(error.response.data, 'target')

      invalidFields.forEach(target => {
        companyDataForm.value.instance.getEditor(target)?.option('isValid', false)
      })
    }
  }
}

const formItems = computed(() => ([
  {
    itemType: 'tabbed',
    name: 'companyTabs',
    tabPanelOptions: {
      deferRendering: false,
      selectedIndex: selectedTabIndex,
      onSelectionChanged: (e) => {
        selectedTabIndex = e.component.option('selectedIndex')
      }
    },
    tabs: [
      {
        title: i18n.t('common.main'),
        colCount: 6,
        tabTemplate: 'validationTabTemplate',
        tabIndex: 0,
        items: [
          {
            itemType: 'group',
            caption: i18n.t('common.mainInfo'),
            colSpan: 2,
            colCount: 2,
            items: [
              { dataField: 'name', label: { text: i18n.t('common.nameAlt') }, editorOptions: { readOnly: true } },
              { dataField: 'name_ext', label: { text: i18n.t('common.nameExtAlt') }, editorOptions: { readOnly: true } },
              { dataField: 'vat_number', label: { text: i18n.t('common.vatNumber') }, editorOptions: { readOnly: true } },
              { dataField: 'tax_code', label: { text: i18n.t('common.taxCode') }, editorOptions: { readOnly: true } },
              { dataField: 'logo_file', label: { text: i18n.t('common.logo') }, template: 'logoFileTemplate', colSpan: 4 }
            ]
          },
          {
            itemType: 'group',
            caption: i18n.t('common.address'),
            colSpan: 2,
            colCount: 2,
            items: [
              {
                dataField: 'address.country',
                template: 'countryEditor',
                label: { text: i18n.t('common.country') },
                validationRules: [{ type: 'required' }],
                editorOptions: { tabIndex: 0 }
              },
              {
                dataField: 'address.state',
                template: 'stateEditor',
                label: { text: i18n.t('common.state') },
                editorOptions: { tabIndex: 0 }
              },
              {
                dataField: 'address.city',
                label: { text: i18n.t('common.city') },
                template: 'cityEditor',
                validationRules: [{ type: 'required' }],
                editorOptions: { tabIndex: 0 }
              },
              {
                dataField: 'address.zip_code',
                label: { text: i18n.t('common.zipCode') },
                setCellValue: (newData, value) => {
                  dataGrid.useZipCode(newData, value, 'address')
                },
                validationRules: [{ type: 'required' }],
                editorOptions: { tabIndex: 0 }
              },
              {
                colSpan: 2,
                dataField: 'address.street',
                label: { text: i18n.t('common.street') },
                validationRules: [{ type: 'required' }],
                editorOptions: { tabIndex: 0 }
              },
            ]
          },
          {
            itemType: 'group',
            caption: i18n.t('common.companyData'),
            colSpan: 2,
            colCount: 2,
            items: [
              { dataField: 'email', label: { text: i18n.t('common.email') }, validationRules: [{ type: 'email' }] },
              { dataField: 'phone', label: { text: i18n.t('common.phone') }, editorOptions: { onContentReady: (e) => { dataGrid.usePhoneMask(e.element) }}},
              { dataField: 'mobile', label: { text: i18n.t('common.mobile') }, editorOptions: { tabIndex: 0, onContentReady: (e) => { dataGrid.usePhoneMask(e.element) }}},
              { dataField: 'fax', label: { text: i18n.t('common.fax') }, editorOptions: { mask: dataGrid.useMask().mobileMask } },
              { dataField: 'social_capital', editorType: 'dxNumberBox', label: { text: i18n.t('common.socialCapital') }, editorOptions: { min: 0, format: '#,##0.##' } },
              { dataField: 'tax_regime', label: { text: i18n.t('common.taxRegime') }, editorOptions: { maxLength: 6 } },
            ]
          },
          {
            itemType: 'group',
            caption: i18n.t('common.default', 2),
            colSpan: 6,
            colCount: 6,
            items: [
                {
                  dataField: 'default_currency_ref',
                  editorType: 'dxSelectBox',
                  label: { text: i18n.t('common.currency') },
                  editorOptions: {
                    dataSource: {
                      store: webSiteStore.getLocalizedCurrencies(),
                      sort: 'code',
                    },
                    valueExpr: 'id',
                    displayExpr: codeNameDisplayExpr,
                    searchEnabled: true,
                    onValueChanged(e) {
                      updateMinCurrencyPrecision(e.value)
                    }
                  }
                },
                {
                  dataField: 'default_vat_type_ref',
                  editorType: 'dxSelectBox',
                  label: { text: i18n.t('common.vat') },
                  editorOptions: {
                    dataSource: {
                      store: webSiteStore.getLocalizedVatTypes(),
                      sort: 'code',
                    },
                    valueExpr: 'id',
                    displayExpr: codeNameDisplayExpr,
                    searchEnabled: true,
                    showClearButton: true,
                  }
                },
                {
                  dataField: 'default_unit_of_measure_ref',
                  editorType: 'dxSelectBox',
                  label: { text: i18n.t('common.unitOfMeasure') },
                  editorOptions: {
                    dataSource: {
                      store: webSiteStore.getLocalizedUnitsOfMeasure(),
                      sort: 'code',
                    },
                    valueExpr: 'id',
                    displayExpr: codeNameDisplayExpr,
                    searchEnabled: true,
                  }
                },
                {
                  dataField: 'default_vat_country',
                  template: 'countryEditor',
                  label: { text: i18n.t('common.vatCountry') },
                },
                {
                  dataField: 'default_supplier_brand_ref',
                  editorType: 'dxSelectBox',
                  label: { text: i18n.t('common.defaultBrand') },
                  editorOptions: {
                    dataSource: {
                      store: registryStore.supplierBrands,
                      sort: 'code',
                    },
                    valueExpr: 'id',
                    displayExpr: codeNameDisplayExpr,
                    searchEnabled: true,
                    showClearButton: true,
                  }
                },
                {
                  itemType: 'empty',
                },
                {
                  dataField: 'deposit',
                  editorType: 'dxSelectBox',
                  label: { text: i18n.t('common.deposit') },
                  editorOptions: {
                    dataSource: {
                      store: registryStore.getLocalizedDeposits(),
                      sort: 'code',
                    },
                    valueExpr: 'id',
                    displayExpr: codeNameDisplayExpr,
                    searchEnabled: true,
                  }
                },
                {
                  dataField: 'garage',
                  editorType: 'dxSelectBox',
                  label: { text: i18n.t('common.garage') },
                  editorOptions: {
                    dataSource: {
                      store: registryStore.getLocalizedDeposits(),
                      sort: 'code',
                    },
                    valueExpr: 'id',
                    displayExpr: codeNameDisplayExpr,
                    searchEnabled: true,
                  }
                },
                {
                  dataField: 'entity_lock_duration_minutes',
                  editorType: 'dxSlider',
                  label: {
                    template: (data, container) => {
                      const labelEl = document.createElement('span')
                      const value = data.component.option('formData').entity_lock_duration_minutes
                      labelEl.textContent = i18n.t('common.entityLockDurationMinutes', {value})
                      labelEl.className = 'dx-field-item-label-text'
                      container.appendChild(labelEl)
                      data.component.on('fieldDataChanged', (e) => {
                        if (e.dataField === 'entity_lock_duration_minutes') {
                          labelEl.textContent = i18n.t('common.entityLockDurationMinutes', {value: e.value})
                        }
                      })
                    }
                  },
                  editorOptions: {
                    min: 1,
                    max: 20,
                    step: 1
                  }
                },
                {
                  dataField: 'default_display_price_precision',
                  editorType: 'dxSlider',
                  label: {
                    template: (data, container) => {
                      const labelEl = document.createElement('span')
                      const value = data.component.option('formData').default_display_price_precision
                      labelEl.textContent = i18n.t('common.unitPricePrecision', {value})
                      labelEl.className = 'dx-field-item-label-text'
                      container.appendChild(labelEl)
                      data.component.on('fieldDataChanged', (e) => {
                        if (e.dataField === 'default_display_price_precision') {
                          labelEl.textContent = i18n.t('common.unitPricePrecision', {value: e.value})
                        }
                      })
                    }
                  },
                  editorOptions: {
                    min: minPrecision.value,
                    max: 6,
                    step: 1,
                  }
                },
            ]
          },
        ]
      },
      webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_SCHEDULER_READ) ? {
        title: i18n.t('common.scheduler'),
        colCount: 6,
        tabIndex: 1,
        items: [
          {
            itemType: 'group',
            caption: i18n.t('common.default', 2),
            colSpan: 6,
            colCount: 6,
            items: [
              {
                dataField: 'default_scheduler_daily_view_days',
                editorType: 'dxSlider',
                label: {
                  template: (data, container) => {
                    const labelEl = document.createElement('span')
                    const value = data.component.option('formData').default_scheduler_daily_view_days
                    labelEl.textContent = i18n.t('common.defaultSchedulerDailyViewDays', {value})
                    labelEl.className = 'dx-field-item-label-text'
                    container.appendChild(labelEl)
                    data.component.on('fieldDataChanged', (e) => {
                      if (e.dataField === 'default_scheduler_daily_view_days') {
                        labelEl.textContent = i18n.t('common.defaultSchedulerDailyViewDays', {value: e.value})
                      }
                    })
                  }
                },
                editorOptions: {
                  min: 1,
                  max: 20,
                  step: 1
                }
              },
              {
                dataField: 'default_scheduler_first_day',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultSchedulerFirstDay') },
                editorOptions: {
                  dataSource: { store: webSiteStore.getLocalizedWeekDays() },
                  valueExpr: 'id',
                  displayExpr: 'name',
                }
              },
              {
                dataField: 'default_scheduler_start_hour',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultSchedulerStartHour') },
                editorOptions: {
                  dataSource: Array.from({ length: 24 }, (_, i) => i),
                  displayExpr: (hour) => `${hour}:00`,
                  value: companyData.value.default_scheduler_start_hour,
                  dropDownButtonTemplate: () => "<span class='mdi mdi-clock-edit-outline'></span>"
                },
                validationRules: [{
                  type: 'custom',
                  message: i18n.t('common.startHourMustBeLess'),
                  validationCallback: (e) => {
                    const end = companyDataForm?.value?.formData?.default_scheduler_end_hour
                    return e.value < end
                  }
                }]
              },
              {
                dataField: 'default_scheduler_end_hour',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultSchedulerEndHour') },
                editorOptions: {
                  dataSource: Array.from({ length: 24 }, (_, i) => i + 1),
                  displayExpr: (hour) => `${hour}:00`,
                  value: companyData.value.default_scheduler_end_hour,
                  dropDownButtonTemplate: () => "<span class='mdi mdi-clock-edit-outline'></span>"
                },
                validationRules: [{
                  type: 'custom',
                  message: i18n.t('common.endHourMustBeGreater'),
                  validationCallback: (e) => {
                    const start = companyDataForm?.value?.formData?.default_scheduler_start_hour
                    return e.value > start
                  }
                }]
              }
            ]
          },
        ]
      } : {},
      {
        title: i18n.t('common.order', 2),
        colCount: 6,
        tabTemplate: 'validationTabTemplate',
        tabIndex: 1,
        items: [
          {
            itemType: 'group',
            caption: i18n.t('common.default', 2),
            colSpan: 6,
            colCount: 6,
            items: [
              {
                dataField: 'default_order_supplier_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultSupplier') },
                editorOptions: {
                  dataSource: {
                    store: registryStore.suppliers,
                    sort: 'code',
                  },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  onValueChanged: (e) => {
                    companyData.value.default_order_supplier_division_ref = null
                    companyData.value.default_order_supplier_order_reason_ref = null
                    updateLookupDataSource(e.value, 'default_order_supplier_division_ref', getFilterBySupplier(e.value))
                    updateLookupDataSource(e.value, 'default_order_supplier_order_reason_ref', getFilterBySupplierAndSupplierDivision(e.value))
                  },
                  showClearButton: true,
                  searchEnabled: true,
                }
              },
              {
                dataField: 'default_order_supplier_division_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultDivision') },
                editorOptions: {
                  dataSource: {
                    store: registryStore.supplierDivisions,
                    filter: getFilterBySupplier(companyData.value.default_order_supplier_ref),
                    sort: 'code',
                  },
                  valueExpr: 'id',
                  onValueChanged: (e) => {
                    companyData.value.default_order_supplier_order_reason_ref = null
                    updateLookupDataSource(e.value, 'default_order_supplier_order_reason_ref', getFilterBySupplierAndSupplierDivision(companyData.value.default_order_supplier_ref, e.value), false)
                  },
                  displayExpr: codeNameDisplayExpr,
                  readOnly: !companyData.value.default_order_supplier_ref,
                  showClearButton: true,
                  searchEnabled: true,
                },
              },
              {
                dataField: 'default_order_supplier_order_reason_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultOrderReason') },
                editorOptions: {
                  dataSource: {
                    store: orderStore.getLocalizedSupplierOrderReasons(),
                    filter: getFilterBySupplierAndSupplierDivision(companyData.value.default_order_supplier_ref),
                    sort: 'code',
                  },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  readOnly: !companyData.value.default_order_supplier_ref,
                  showClearButton: true,
                  searchEnabled: true,
                },
              },
              {
                dataField: 'default_order_payment_type_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultPaymentType') },
                editorOptions: {
                  dataSource: {
                    store: webSiteStore.getLocalizedPaymentTypes(),
                    sort: 'code',
                  },
                  searchEnabled: true,
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  showClearButton: true,
                },
              },
              { dataField: 'order_auto_insert', editorType: 'dxCheckBox', label: { text: i18n.t('common.autoInsertOrder') } },
            ]
          },
        ]
      },
      (webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_WORKSHEET_READ) || webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_QUOTE_READ)) ? {
        title: i18n.t('common.garage', 2),
        colCount: 6,
        tabTemplate: 'validationTabTemplate',
        tabIndex: 2,
        items: [
          webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_QUOTE_READ) ? {
            itemType: 'group',
            caption: `${i18n.t('common.default', 2)} - ${i18n.t('common.quote', 2)}`,
            colSpan: 3,
            colCount: 3,
            items: [
              {
                dataField: 'default_quote_garage_reason_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultWorkReason') },
                editorOptions: {
                  dataSource: {
                    store: garageStore.getLocalizedGarageReasons(),
                    sort: 'code',
                  },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  searchEnabled: true,
                }
              },
            ]
          } : {},
          webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_WORKSHEET_READ) ? {
            itemType: 'group',
            caption: `${i18n.t('common.default', 2)} - ${i18n.t('common.worksheet', 2)}`,
            colSpan: 3,
            colCount: 3,
            items: [
              {
                dataField: 'default_worksheet_garage_reason_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultWorkReason') },
                editorOptions: {
                  dataSource: {
                    store: garageStore.getLocalizedGarageReasons(),
                    sort: 'code',
                  },
                  searchEnabled: true,
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                }
              },
              {
                dataField: 'default_worksheet_document_price_type',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.documentPriceType') },
                editorOptions: {
                  dataSource: { store: portalStore.getLocalizedDocumentPriceTypes() },
                  valueExpr: 'id',
                  displayExpr: 'name',
                }
              },
              {
                dataField: 'allow_work_time_entries_on_closed_worksheet',
                editorType: 'dxCheckBox',
                label: { text: i18n.t('common.allowWorkTimeEntriesOnClosedWorksheet') }
              }
            ]
          } : {},
          webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_WORKSHEET_READ) ? {
            itemType: 'group',
            caption: i18n.t('common.interventionType', 2),
            colCount: 3,
            colSpan: 3,
            items: [
              {
                colSpan: 3,
                template: 'interventionTypeGridTemplate'
              },
            ]
          } : {},
          webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_WORKSHEET_READ) ? {
            itemType: 'group',
            caption: `${i18n.t('common.documentProgressStatus', 2)} - ${i18n.t('common.worksheet')}`,
            colCount: 3,
            colSpan: 3,
            items: [
              {
                colSpan: 3,
                template: 'documentProgressStatusGridTemplate'
              },
            ]
          } : {},
        ]
      } : {},
      {
        title: i18n.t('common.transmission', 2),
        colCount: 6,
        tabTemplate: 'validationTabTemplate',
        tabIndex: 2,
        items: [
          {
            itemType: 'group',
            caption: i18n.t('common.default', 2),
            colSpan: 6,
            colCount: 6,
            items: [
              {
                dataField: 'default_transmission_supplier_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultSupplier') },
                editorOptions: {
                  dataSource: {
                    store: registryStore.suppliers,
                    sort: 'code',
                  },
                  searchEnabled: true,
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  onValueChanged: (e) => {
                    companyData.value.default_transmission_supplier_division_ref = null
                    companyData.value.default_transmission_supplier_order_reason_ref = null
                    updateLookupDataSource(e.value, 'default_transmission_supplier_division_ref', getFilterBySupplier(e.value))
                    updateLookupDataSource(e.value, 'default_transmission_supplier_order_reason_ref', getFilterBySupplierAndSupplierDivision(e.value))
                  },
                }
              },
              {
                dataField: 'default_transmission_supplier_division_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultDivision') },
                editorOptions: {
                  dataSource: {
                    store: registryStore.supplierDivisions,
                    filter: getFilterBySupplier(companyData.value.default_transmission_supplier_ref),
                    sort: 'code',
                  },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  onValueChanged: (e) => {
                    companyData.value.default_transmission_supplier_order_reason_ref = null
                    updateLookupDataSource(e.value, 'default_transmission_supplier_order_reason_ref', getFilterBySupplierAndSupplierDivision(companyData.value.default_transmission_supplier_ref, e.value), false)
                  },
                  readOnly: !companyData.value.default_transmission_supplier_ref,
                  showClearButton: true,
                  searchEnabled: true,
                },
              },
              {
                dataField: 'default_transmission_supplier_order_reason_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultTransmissionReason') },
                editorOptions: {
                  dataSource: {
                    store: orderStore.getLocalizedSupplierOrderReasons(),
                    filter: getFilterBySupplierAndSupplierDivision(companyData.value.default_transmission_supplier_ref),
                    sort: 'code',
                  },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  readOnly: !companyData.value.default_transmission_supplier_ref,
                  showClearButton: true,
                  searchEnabled: true,
                },
              },
              { dataField: 'transmission_auto_insert', editorType: 'dxCheckBox', label: { text: i18n.t('common.autoInsertTransmission')} },
            ]
          },
        ]
      },
      {
        title: i18n.t('common.subject', 2),
        colCount: 6,
        tabTemplate: 'validationTabTemplate',
        tabIndex: 2,
        items: [
        {
          itemType: 'group',
          caption: i18n.t('common.default', 2),
          colSpan: 6,
          colCount: 6,
          items: [
            { dataField: 'subject_unique_name_surname', editorType: 'dxCheckBox', label: { text: i18n.t('common.uniqueNameSurname')} },
            { dataField: 'subject_validate_vat_and_fiscal_code', editorType: 'dxCheckBox', label: { text: i18n.t('common.validateVatAndFiscalCode')} },
            { dataField: 'subject_clear_customer_export_flag', editorType: 'dxCheckBox', label: { text: i18n.t('common.clearCustomerExportFlag')} },
          ]
        }
        ]
      },
      webSiteStore.hasClaim(webSiteStore.claimsEnum.VEHICLE_REGISTRY_READ) ? {
        title: i18n.t('common.vehicle', 2),
        colCount: 6,
        tabTemplate: 'validationTabTemplate',
        tabIndex: 2,
        items: [
        {
          itemType: 'group',
          caption: i18n.t('common.default', 2),
          colSpan: 6,
          colCount: 6,
          items: [
            { dataField: 'vehicle_plate_allow_blank_spaces', editorType: 'dxCheckBox', label: { text: i18n.t('common.vehiclePlateAllowsBlankSpaces')} },
          ]
        }
        ]
      } : {},
      (webSiteStore.hasClaim(webSiteStore.claimsEnum.SALE_DIRECT_READ) &&
        webSiteStore.hasClaim(webSiteStore.claimsEnum.SALE_INVOICE_READ) &&
          webSiteStore.hasClaim(webSiteStore.claimsEnum.SALE_COMMERCIAL_READ)) ? {
          title: i18n.t('common.saleDocument', 2),
          colCount: 6,
          tabTemplate: 'validationTabTemplate',
          tabIndex: 2,
          items: [
          {
            itemType: 'group',
            caption: `${i18n.t('common.default', 2)}`,
            colSpan: 3,
            colCount: 3,
            items: [
              {
                dataField: 'default_sale_document_sale_reason_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultSaleReason') },
                editorOptions: {
                  dataSource: {
                    store: saleStore.getLocalizedSaleReasons(),
                    sort: 'code',
                  },
                  searchEnabled: true,
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  showClearButton: true,
                }
              },
              {
                dataField: 'default_sale_document_price_type',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.documentPriceType') },
                editorOptions: {
                  dataSource: { store: portalStore.getLocalizedDocumentPriceTypes() },
                  valueExpr: 'id',
                  displayExpr: 'name',
                  showClearButton: true,
                }
              },
              {
                dataField: 'default_sale_document_deposit',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.deposit') },
                editorOptions: {
                  dataSource: {
                    store: registryStore.getLocalizedDeposits(),
                    sort: 'code',
                  },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  searchEnabled: true,
                  showClearButton: true,
                }
              },
              {
                dataField: 'default_sale_document_payment_type_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.defaultPaymentType') },
                editorOptions: {
                  dataSource: {
                    store: webSiteStore.getLocalizedPaymentTypes(),
                    sort: 'code',
                  },
                  searchEnabled: true,
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  showClearButton: true,
                },
              },
            ]
          },
          webSiteStore.hasClaim(webSiteStore.claimsEnum.SALE_INVOICE_ELECTRONIC) ? {
            itemType: 'group',
            caption: `${i18n.t('common.billing')}`,
            colSpan: 2,
            colCount: 3,
            items: [
              {
                colSpan: 3,
                dataField: 'default_sale_document_stamp_vat_type_ref',
                editorType: 'dxSelectBox',
                label: { text: i18n.t('common.vatExemption') },
                editorOptions: {
                  dataSource: {
                    store: webSiteStore.getLocalizedVatTypes().filter(v => v.stamp_required),
                    sort: 'code',
                  },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  searchEnabled: true,
                  showClearButton: true,
                }
              },
            ]
          } : {},
          webSiteStore.hasClaim(webSiteStore.claimsEnum.SALE_INVOICE_ELECTRONIC) ? {
            itemType: 'group',
            caption: `${i18n.t('common.sendingSdi', 2)}`,
            items: [
              {
                dataField: 'electronic_invoice_is_foreign_customer_blocked',
                editorType: 'dxCheckBox',
                label: { text: i18n.t('common.internationalCustomerSendingBlock') },
              },
            ]
          } : {},
          {
            itemType: 'group',
            caption: i18n.t('common.editableLegalNotices'),
            colSpan: 3,
            items: [
              { dataField: 'default_sale_document_footer_text', editorType: 'dxTextArea', label: { text: i18n.t('common.footerNote')}, editorOptions: { height: '6rem' } },
            ]
          },
          {
            itemType: 'group',
            caption: i18n.t('common.excludeSaleReason', 2),
            colSpan: 2,
            items: [
              {
                template: 'saleReasonGridTemplate'
              },
            ]
          },
          ]
        } : {},
      (webSiteStore.hasClaim(webSiteStore.claimsEnum.SALE_DIRECT_READ) &&
        webSiteStore.hasClaim(webSiteStore.claimsEnum.SALE_INVOICE_READ) &&
          webSiteStore.hasClaim(webSiteStore.claimsEnum.SALE_COMMERCIAL_READ)) ? {
          title: i18n.t('common.taxation'),
          colCount: 6,
          tabTemplate: 'validationTabTemplate',
          tabIndex: 2,
          items: [
        {
          itemType: 'group',
          caption: i18n.t('common.default', 2),
          colSpan: 6,
          colCount: 6,
          items: [
            { dataField: 'default_sale_document_stamp_account_code', label: { text: i18n.t('common.stampAccountCode')} },
          ]
        }
          ]
        } : {},
        {
          title: i18n.t('common.companyData.privacy.privacyNote'),
          colCount: 6,
          tabTemplate: 'validationTabTemplate',
          tabIndex: 9,
          items: [
        {
          itemType: 'group',
          caption: i18n.t('common.companyData.privacy.dataController'),
          colSpan: 2,
          colCount: 6,
          items: [
            { dataField: 'data_controller_name', colSpan: 2, label: { text: i18n.t('common.companyName')}, validationRules: [{ type: 'required' }], editorOptions: { tabIndex: 9 } },
            { dataField: 'data_controller_branch_address', colSpan: 2, label: { text: i18n.t('common.companyData.privacy.branchRegistered')}, validationRules: [{ type: 'required' }], editorOptions: { tabIndex: 9 } },
            { dataField: 'data_controller_contact_details', colSpan: 2, label: { text: i18n.t('common.contactDetails')} },
            { dataField: 'data_controller_email', colSpan: 2, label: { text: i18n.t('common.email')}, validationRules: [{ type: 'required' }, { type: 'email' }], editorOptions: { tabIndex: 9 } },
            { dataField: 'data_controller_pec', colSpan: 2, label: { text: i18n.t('common.emailCertified')}, validationRules: [{ type: 'email' }], editorOptions: { tabIndex: 9 } },
            { dataField: 'data_controller_tax_code', colSpan: 2, label: { text: i18n.t('common.taxCode')} },
            { dataField: 'data_controller_vat_number', colSpan: 2, label: { text: i18n.t('common.vatNumber')} },
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.externalDataProcessor', 2),
          colSpan: 3,
          items: [
            {
              template: 'externalPrivacyDataProcessorGridTemplate'
            },
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.companyData.privacy.privacyContact'),
          colSpan: 1,
          colCount: 1,
          items: [
            { dataField: 'data_controller_privacy_responsible', label: { text: i18n.t('common.controller')}, validationRules: [{ type: 'required' }], editorOptions: { tabIndex: 9 } },
            { dataField: 'data_controller_privacy_email', label: { text: i18n.t('common.email')}, validationRules: [{ type: 'required' }, { type: 'email' }], editorOptions: { tabIndex: 9 } },
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.companyData.privacy.dpoSection'),
          colSpan: 2,
          items: [
            {
              dataField: 'has_dpo',
              editorType: 'dxRadioGroup',
              label: { text: i18n.t('common.companyData.privacy.dpoSection') },
              editorOptions: {
                items: [
                  { value: true, text: i18n.t('common.yes') },
                  { value: false, text: i18n.t('common.no') }
                ],
                valueExpr: 'value',
                displayExpr: 'text',
                layout: 'horizontal',
                tabIndex: 9,
              },
            },
            {
              itemType: 'group',
              name: 'dpo_details_group',
              cssClass: 'dpo-details-custom',
              colCount: 2,
              items: [
                {
                  dataField: 'dpo_name',
                  colSpan: 1,
                  label: { text: i18n.t('common.companyName') },
                  validationRules: [
                    {
                      type: 'custom',
                      message: i18n.t('common.required'),
                      validationCallback: (e) => {
                        if (!companyData.value?.has_dpo) return true
                        return e.value != null && e.value !== ''
                      }
                    }
                  ],
                  editorOptions: { tabIndex: 9 }
                },
                {
                  dataField: 'dpo_branch_address',
                  colSpan: 1,
                  label: { text: i18n.t('common.companyData.privacy.branchRegistered') },
                  validationRules: [
                    {
                      type: 'custom',
                      message: i18n.t('common.required'),
                      validationCallback: (e) => {
                        if (!companyData.value?.has_dpo) return true
                        return e.value != null && e.value !== ''
                      }
                    }
                  ],
                  editorOptions: { tabIndex: 9 }
                },
                {
                  dataField: 'dpo_contact_details',
                  colSpan: 1,
                  label: { text: i18n.t('common.contactDetails') }
                },
                {
                  dataField: 'dpo_email',
                  colSpan: 1,
                  label: { text: i18n.t('common.email') },
                  validationRules: [
                    {
                      type: 'custom',
                      message: i18n.t('common.required'),
                      validationCallback: (e) => {
                        if (!companyData.value?.has_dpo) return true
                        return e.value != null && e.value !== ''
                      }
                    },
                    { type: 'email' }
                  ],
                  editorOptions: { tabIndex: 9 }
                },
                {
                  dataField: 'dpo_email_certified',
                  colSpan: 1,
                  label: { text: i18n.t('common.emailCertified') },
                  validationRules: [{ type: 'email' }],
                  editorOptions: { tabIndex: 9 }
                },
                {
                  dataField: 'dpo_tax_code',
                  colSpan: 1,
                  label: { text: i18n.t('common.taxCode') }
                },
                {
                  dataField: 'dpo_vat_number',
                  colSpan: 1,
                  label: { text: i18n.t('common.vatNumber') }
                }

              ]
            }
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.companyData.privacy.dataControllerDetails'),
          colSpan: 3,
          colCount: 3,
          items: [
            {
              dataField: 'data_controller_data_type',
              editorType: 'dxTagBox',
              colSpan: 2,
              label: { text: i18n.t('common.companyData.privacy.typeOfPersonalDataProcessed', 2) },
              editorOptions: {
                items: registryStore.getLocalizedDataControllerDataTypes().map(d => ({ id: d.id, text: i18n.t(d.name) })),
                displayExpr: 'text',
                valueExpr: 'id',
                showSelectionControls: true,
                tabIndex: 9,
              },
              validationRules: [
                {
                  type: 'custom',
                  message: i18n.t('common.atLeastOneRequired'),
                  validationCallback: function(e) {
                    const formData = companyDataForm?.value?.formData

                    const tagBoxHasValue =
                      e.value.length > 0

                    const otherHasValue =
                      formData?.data_controller_data_type_other &&
                      formData?.data_controller_data_type_other.trim() !== ''

                    return tagBoxHasValue || otherHasValue
                  }
                }
              ],
            },
            {
              dataField: 'data_controller_data_type_other',
              label: { text: i18n.t('common.other') },
              editorOptions: {
                onValueChanged: (e) => {
                  companyDataForm.value.instance.validate()
                },
                tabIndex: 9,
              }
            },
            {
              dataField: 'data_controller_processing_methods',
              editorType: 'dxTagBox',
              colSpan: 2,
              label: { text: i18n.t('common.companyData.privacy.processingMethods', 2) },
              editorOptions: {
                items: registryStore.getLocalizedDataControllerProcessingMethods().map(d => ({ id: d.id, text: i18n.t(d.name) })),
                displayExpr: 'text',
                valueExpr: 'id',
                showSelectionControls: true,
                tabIndex: 9,
              },
              validationRules: [
                { type: 'required' }
              ]
            },
            {itemType: 'empty', colSpan: 1},
            {
              dataField: 'data_controller_legal_basis',
              editorType: 'dxTagBox',
              colSpan: 2,
              label: { text: i18n.t('common.companyData.privacy.processingPurposesLegalBasis', 2) },
              editorOptions: {
                items: registryStore.getLocalizedDataControllerLegalBasis().map(d => ({ id: d.id, text: i18n.t(d.name) })),
                displayExpr: 'text',
                valueExpr: 'id',
                showSelectionControls: true,
                tabIndex: 9,
              },
              validationRules: [
                { type: 'required' }
              ]
            },
            {itemType: 'empty', colSpan: 1},
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.dataConservationPeriod'),
          colSpan: 1,
          colCount: 6,
          items: [
            { dataField: 'data_controller_years_of_conservation', editorType: 'dxNumberBox', label: { text: i18n.t('common.year', 2)}, editorOptions: { min: 0, step: 1 } },
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.dataTransferOutsideEEA'),
          colSpan: 2,
          colCount: 1,
          items: [
            {
              dataField: 'data_transfer_outside_eea',
              editorType: 'dxRadioGroup',
              label: { text: i18n.t('common.dataTransferOutsideEEA') },
              editorOptions: {
                items: [
                  { value: true, text: i18n.t('common.yes') },
                  { value: false, text: i18n.t('common.no') }
                ],
                valueExpr: 'value',
                displayExpr: 'text',
                layout: 'horizontal',
                tabIndex: 9,
              },
            },
            {
              dataField: 'data_transfer_mechanisms',
              editorType: 'dxTagBox',
              label: { text: i18n.t('common.companyData.privacy.transferMechanisms') },
              editorOptions: {
                items: registryStore.getLocalizedDataControllerDataTransferMechanisms().map(d => ({ id: d.id, text: i18n.t(d.name) })),
                displayExpr: 'text',
                valueExpr: 'id',
                showSelectionControls: true,
                tabIndex: 9,
              },
              validationRules: [
                {
                  type: 'custom',
                  message: i18n.t('common.selectAtLeastOneMechanism'),
                  validationCallback: function(e) {
                    if (!companyData.value?.data_transfer_outside_eea) {
                      return true
                    }

                    return (
                      (companyData.value?.data_transfer_mechanisms && companyData.value.data_transfer_mechanisms.length > 0) ||
                      e.value.length > 0
                    )
                  }
                }
              ]
            }

          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.print'),
          colSpan: 3,
          items: [
            { dataField: 'print_privacy_template_adopted', editorType: 'dxCheckBox', label: { text: i18n.t('common.printTemplateAdopt', 2)} },
          ]
        },
          ]
        }
    ]
  },
]))

const onStateChanged = (e) => {
  if (e.value) {
    const newData = companyData.value
    newData.address = newData.address || {}
    newData.address.state = newData.address.state_text = newData.address.city = newData.address.city_text = newData.address.zip_code = null

    const currentCountry = webSiteStore.countries.find(x => x.id === newData.address?.country)
    if (currentCountry?.has_structured_location_data) {
      newData.address.state = e.value?.id
    } else {
      newData.address.state_text = e.value
    }
  }
}

const onCityChanged = (e) => {
  if (e.value) {
    const newData = companyData.value
    newData.address.zip_code = newData.address.city = newData.address.city_text = null

    const currentCountry = webSiteStore.countries.find(x => x.id === newData.address?.country)
    if (currentCountry?.has_structured_location_data) {
      newData.address.state = e.value?.state
      newData.address.city = e.value?.id
      newData.address.zip_code = e.value?.zip_code
    } else {
      newData.address.city_text = e.value
    }
  }
}

const onCountryChanged = (e, cellInfo) => {
  if (e.value && cellInfo.dataField === 'address.country') {
    companyData.value.address = companyData.value.address || {}
    companyData.value.address.country = e.value.id
    companyData.value.address.state = companyData.value.address.state = companyData.value.address.state_text = companyData.value.address.city = companyData.value.address.city_text = companyData.value.address.zip_code = null
    updateLookupDataSource(e.value, 'address.state', ['country', '=', e.value.id])
  }
  if (cellInfo.dataField === 'default_vat_country') {
    companyData.value.default_vat_country = e.value?.id || null
  }
}

const onFieldDataChanged = (e) => {
  const form = e.component

  if (e.dataField === 'data_transfer_outside_eea') {
    const dataTransferMechanismsTagBox = form.getEditor('data_transfer_mechanisms')
    if (dataTransferMechanismsTagBox) {
      dataTransferMechanismsTagBox.option('disabled', !e.value)
    }

    if (!e.value) {
      form.updateData('data_transfer_mechanisms', [])
    }
  }

  if (e.dataField === 'has_dpo') {
    const disabled = !e.value

    dpo_fields.forEach(field => {
      const editor = form.getEditor(field)

      if (editor) {
        editor.option('disabled', disabled)
      }

      if (disabled) {
        form.updateData(field, null)
      }
    })
  }
}

</script>

<template>
  <div>
    <q-dialog
      v-model="confirmDialog"
      persistent
    >
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar
            icon="mdi-alert"
            color="orange"
            text-color="white"
          />
          <span class="q-ml-sm">{{ $t('common.confirmSave') }}</span>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            v-close-popup
            flat
            :label="i18n.t('common.cancel')"
            color="primary"
          />
          <q-btn
            v-close-popup
            flat
            :label="i18n.t('common.save')"
            color="primary"
            @click="onSave"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <dx-form
      ref="companyDataForm"
      :key="companyDataFormKey"
      class="q-mt-md"
      :items="formItems"
      :form-data="companyData"
      :validation-group="companyDataValidationGroup"
      @field-data-changed="onFieldDataChanged"
      @content-ready="onFormReady"
    >
      <template #countryEditor="{ data: cellInfo }">
        <span style="display: none;">{{ companyData?.address?.country }}</span>
        <country-drop-down-box
          :key="['default_vat_country'].includes(cellInfo?.dataField) ? companyData?.default_vat_country : companyData?.address?.country"
          :show-clear-button="['default_vat_country'].includes(cellInfo?.dataField)"
          :value="['default_vat_country'].includes(cellInfo?.dataField) ? companyData?.default_vat_country : companyData?.address?.country"
          :is-portal="['default_vat_country'].includes(cellInfo?.dataField)"
          :validation-group="companyDataValidationGroup"
          :validation-rules="['address.country'].includes(cellInfo?.dataField) ? requiredValidationRules : []"
          @value-changed="onCountryChanged($event, cellInfo)"
        />
      </template>
      <template #stateEditor>
        <span style="display: none;">{{ companyData?.address?.state }}</span>
        <state-drop-down-box
          :key="companyData?.address?.state"
          :value="companyData?.address?.state"
          :value-text="companyData?.address?.state_text"
          :country="companyData?.address?.country"
          @value-changed="onStateChanged"
        />
      </template>
      <template #cityEditor>
        <span style="display: none;">{{ companyData?.address?.city }}</span>
        <city-drop-down-box
          :key="companyData?.address?.city"
          :value="companyData?.address?.city"
          :value-text="companyData?.address?.city_text"
          :state="companyData?.address?.state"
          :country="companyData?.address?.country"
          :validation-group="companyDataValidationGroup"
          :validation-rules="requiredValidationRules"
          @value-changed="onCityChanged"
        />
      </template>
      <template #validationTabTemplate="{data: templateOptions}">
        <ValidationTab
          :title="templateOptions.title"
          :has-error="validationTabIndexErrors.includes(templateOptions.tabIndex)"
        />
      </template>
      <template #interventionTypeGridTemplate>
        <intervention-type-grid />
      </template>
      <template #saleReasonGridTemplate>
        <dx-tag-box
          v-if="saleReasonsDataSource"
          v-model="excludedSaleReasons"
          :data-source="saleReasonsDataSource"
          value-expr="id"
          :display-expr="codeNameDisplayExpr"
          :search-enabled="true"
          :show-clear-button="true"
          :show-selection-controls="true"
          :show-drop-down-button="true"
          apply-value-mode="useButtons"
          :multiline="true"
          :label="i18n.t('common.excludeSaleReason')"
          label-mode="outside"
        />
      </template>
      <template #logoFileTemplate>
        <div>
          <q-file
            v-model="logoFile"
            flat
            dense
            accept="image/*"
            :max-file-size="maxLogoSize"
            :error="!!logoFileError"
            :error-message="logoFileError"
            clearable
            @update:model-value="logoFileError = null"
            @rejected="logoFileError = i18n.t('common.fileTooLarge', [formatBytes(maxLogoSize)])"
          >
            <template #prepend>
              <q-icon
                :name="companyData.logo_file && !logoFile ? 'mdi-image-check' : 'mdi-image-outline'"
                :color="companyData.logo_file && !logoFile ? 'positive' : undefined"
              />
            </template>

            <template
              v-if="companyData.logo_file || logoFile"
              #default
            >
              <div class="row items-center full-width q-gutter-xs no-wrap">
                <q-icon
                  name="mdi-paperclip"
                  size="13px"
                  color="grey-6"
                />
                <span class="text-caption text-grey-7 ellipsis">
                  {{ logoFile ? logoFile.name : (companyData.logo_file).split('/').pop() }}
                </span>
                <q-btn
                  flat
                  round
                  dense
                  size="xs"
                  color="grey-6"
                  icon="mdi-close"
                  class="q-ml-auto"
                  :title="i18n.t('common.delete')"
                  @click.stop="companyData.logo_file = null; logoFileCleared = true"
                />
              </div>
            </template>
          </q-file>
        </div>
      </template>
      <template #documentProgressStatusGridTemplate>
        <document-progress-status-grid />
      </template>
      <template #externalPrivacyDataProcessorGridTemplate>
        <external-privacy-data-processor-grid />
      </template>
    </dx-form>
    <q-btn
      class="q-mt-md float-right"
      color="primary"
      :label="i18n.t('common.save')"
      @click="onConfirmSave"
    />
  </div>
</template>

<style scoped>
  :deep(.dx-field-item .dx-toggle-controls-paddings) {
    padding-left: 0px !important;
  }

  :deep(.dpo-details-custom) {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
</style>
